import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ThemeProvider,
  Box,
  Button,
  Typography,
  Chip,
  Card,
  CardContent,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import type { AppearanceConfig } from '../types/EnvironmentConfig.types'
import type { BrandingConfig } from '../types/EnvironmentConfig.types'
import type { SevvThemeTokens } from '@/theme/sevvThemeTokenModel'
import { resolveEffectivePalette } from '@/theme/sevvThemeTokenModel'
import { buildSevvMuiTheme } from '@/theme/buildSevvTheme'
import { applySevvCssVariables } from '@/theme/applySevvCssVariables'
import { resolveThemeMode } from '@/theme/resolveWorkspaceTheme'

export type ThemePreviewVariant = 'full' | 'compact'

export type BrandingPreviewSlice = Pick<
  BrandingConfig,
  | 'platformName'
  | 'logoUrl'
  | 'authDialogPlatformName'
  | 'authDialogLogoUrl'
  | 'logoAltText'
>

export interface ThemePreviewPanelProps {
  appearance: AppearanceConfig
  designTokens: SevvThemeTokens
  /** Optional platform chrome for Branding tab */
  brandingPreview?: BrandingPreviewSlice | null
  variant?: ThemePreviewVariant
}

const PALETTE_LABELS: Record<keyof SevvThemeTokens['palette'], string> = {
  primary: 'Primary',
  primaryLight: 'Primary light',
  primaryDark: 'Primary dark',
  primaryPale: 'Primary pale',
  secondary: 'Secondary',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
  backgroundDefault: 'Bg app',
  backgroundPaper: 'Bg surface',
  textPrimary: 'Text 1',
  textSecondary: 'Text 2',
  divider: 'Divider',
}

type PreviewModeToggle = 'match' | 'light' | 'dark'

function appearanceForPreviewToggle(
  appearance: AppearanceConfig,
  toggle: PreviewModeToggle
): AppearanceConfig {
  if (toggle === 'match') return appearance
  return {
    ...appearance,
    theme: {
      ...appearance.theme,
      mode: toggle,
    },
  }
}

function resolveLogoSrc(logoUrl: string): string {
  if (!logoUrl) return ''
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('data:')) {
    return logoUrl
  }
  if (typeof window !== 'undefined' && logoUrl.startsWith('/')) {
    return `${window.location.origin}${logoUrl}`
  }
  return logoUrl
}

/**
 * Real-time preview: same buildSevvMuiTheme + scoped CSS variables as production.
 */
const ThemePreviewPanel: React.FC<ThemePreviewPanelProps> = ({
  appearance,
  designTokens,
  brandingPreview,
  variant = 'full',
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [prefersDark, setPrefersDark] = useState(false)
  const [previewModeToggle, setPreviewModeToggle] = useState<PreviewModeToggle>('match')

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setPrefersDark(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const effectiveAppearance = useMemo(
    () => appearanceForPreviewToggle(appearance, previewModeToggle),
    [appearance, previewModeToggle]
  )

  const resolvedPrefersDark = previewModeToggle === 'match' ? prefersDark : false

  const muiTheme = useMemo(
    () => buildSevvMuiTheme(designTokens, effectiveAppearance, resolvedPrefersDark),
    [designTokens, effectiveAppearance, resolvedPrefersDark]
  )

  useLayoutEffect(() => {
    const el = wrapRef.current
    if (el) {
      applySevvCssVariables(el, designTokens, effectiveAppearance, resolvedPrefersDark)
    }
  }, [designTokens, effectiveAppearance, resolvedPrefersDark])

  const paletteEntries = Object.entries(designTokens.palette) as [keyof SevvThemeTokens['palette'], string][]
  const paletteDarkEffective = useMemo(
    () => resolveEffectivePalette(designTokens, 'dark'),
    [designTokens]
  )

  const displayName =
    brandingPreview?.authDialogPlatformName?.trim() ||
    brandingPreview?.platformName?.trim() ||
    'Platform'
  const displayLogo =
    brandingPreview?.authDialogLogoUrl?.trim() || brandingPreview?.logoUrl?.trim() || ''
  const logoAlt = brandingPreview?.logoAltText || displayName

  const isCompact = variant === 'compact'

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        ref={wrapRef}
        data-testid="theme-preview-panel"
        sx={{
          p: isCompact ? 1.5 : 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxHeight: isCompact ? 'none' : { md: 'calc(100vh - 180px)' },
          overflowY: isCompact ? 'visible' : { md: 'auto' },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {isCompact ? 'Preview' : 'Live theme preview'}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={previewModeToggle}
            onChange={(_, v) => v != null && setPreviewModeToggle(v)}
            aria-label="Preview light or dark"
          >
            <ToggleButton value="match">Match settings</ToggleButton>
            <ToggleButton value="light">Light</ToggleButton>
            <ToggleButton value="dark">Dark</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {brandingPreview && (
          <Box
            data-testid="theme-preview-branding-strip"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              mb: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {displayLogo ? (
              <Box
                component="img"
                src={resolveLogoSrc(displayLogo)}
                alt={logoAlt}
                sx={{ width: isCompact ? 28 : 36, height: isCompact ? 28 : 36, objectFit: 'contain', borderRadius: 0.5 }}
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            ) : null}
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
              {displayName}
            </Typography>
          </Box>
        )}

        {!isCompact && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Saved mode: {appearance.theme.mode}
            {appearance.theme.mode === 'auto'
              ? ` (system → ${resolveThemeMode(appearance, prefersDark)})`
              : ''}
          </Typography>
        )}

        {!isCompact && (
          <>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Palette (light base / dark effective)
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
                gap: 0.75,
                mb: 2,
              }}
            >
              {paletteEntries.map(([key, colorLight]) => {
                const colorDark = paletteDarkEffective[key]
                return (
                  <Tooltip
                    key={key}
                    title={`${PALETTE_LABELS[key]} — light: ${colorLight}; dark: ${colorDark}`}
                  >
                    <Box data-testid={`theme-preview-swatch-${key}`}>
                      <Stack direction="row" spacing={0.25} sx={{ mb: 0.25 }}>
                        <Box
                          data-testid={`theme-preview-swatch-${key}-light`}
                          data-preview-color-light={colorLight}
                          sx={{
                            flex: 1,
                            height: 28,
                            borderRadius: '4px 0 0 4px',
                            bgcolor: colorLight,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Box
                          data-testid={`theme-preview-swatch-${key}-dark`}
                          data-preview-color-dark={colorDark}
                          sx={{
                            flex: 1,
                            height: 28,
                            borderRadius: '0 4px 4px 0',
                            bgcolor: colorDark,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: '0.65rem' }}>
                        {PALETTE_LABELS[key]}
                      </Typography>
                    </Box>
                  </Tooltip>
                )
              })}
            </Box>
          </>
        )}

        {isCompact && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {paletteEntries.slice(0, 6).map(([key, colorLight]) => (
              <Tooltip
                key={key}
                title={`${PALETTE_LABELS[key]} — L: ${colorLight} / D: ${paletteDarkEffective[key]}`}
              >
                <Stack direction="row" data-testid={`theme-preview-swatch-${key}`} sx={{ borderRadius: 0.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box
                    data-testid={`theme-preview-swatch-${key}-light`}
                    data-preview-color-light={colorLight}
                    sx={{ width: 11, height: 22, bgcolor: colorLight }}
                  />
                  <Box
                    data-testid={`theme-preview-swatch-${key}-dark`}
                    data-preview-color-dark={paletteDarkEffective[key]}
                    sx={{ width: 11, height: 22, bgcolor: paletteDarkEffective[key] }}
                  />
                </Stack>
              </Tooltip>
            ))}
          </Box>
        )}

        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Typography
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: appearance.typography.fontFamily,
              fontWeight: appearance.typography.fontWeight === 'bold' ? 700 : appearance.typography.fontWeight === 'light' ? 300 : 600,
              fontSize:
                appearance.typography.fontSize === 'small' ? '1rem' : appearance.typography.fontSize === 'large' ? '1.35rem' : '1.25rem',
              color: 'text.primary',
            }}
          >
            Sample heading
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: appearance.typography.fontFamily,
              fontWeight: appearance.typography.fontWeight === 'bold' ? 700 : appearance.typography.fontWeight === 'light' ? 300 : 400,
              fontSize:
                appearance.typography.fontSize === 'small' ? '0.8125rem' : appearance.typography.fontSize === 'large' ? '1.0625rem' : '0.875rem',
              color: 'text.secondary',
            }}
          >
            Body text reflects font family, size, and weight from Appearance.
          </Typography>
        </Box>

        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Components
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <Button variant="contained" color="primary" size="small">
            Primary
          </Button>
          <Button variant="outlined" color="primary" size="small">
            Outlined
          </Button>
          <Chip label="Primary" color="primary" size="small" />
          {!isCompact && <Chip label="Default" size="small" />}
        </Box>

        {!isCompact && (
          <>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Game HUD (in-room)
            </Typography>
            <Box
              data-testid="theme-preview-game-hud-strip"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                mb: 2,
                borderRadius: '12px',
                background: 'var(--sevv-surface-game-toolbar-bg)',
                border: '1px solid var(--sevv-surface-game-toolbar-border)',
                boxShadow: (t) => t.shadows[4],
                backgroundImage: 'none',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: 'var(--sevv-surface-game-icon-btn-bg)',
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'var(--sevv-surface-game-accent-muted)',
                  opacity: 0.35,
                }}
              />
            </Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Dialog chrome (MuiDialogTitle / MuiDialogActions)
            </Typography>
            <Box
              data-testid="theme-preview-dialog-chrome"
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  background: 'var(--sevv-dialog-title-bg)',
                  color: 'var(--sevv-dialog-title-color)',
                  px: 2,
                  py: 1.25,
                  borderBottom: 'var(--sevv-dialog-title-border-bottom)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                Sample dialog title
              </Box>
              <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper' }}>
                <Typography variant="caption" color="text.secondary">
                  Dialog content uses paper background from the theme.
                </Typography>
              </Box>
              <Box
                sx={{
                  background: 'var(--sevv-dialog-actions-bg)',
                  borderTop: 'var(--sevv-dialog-actions-border-top)',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Dialog actions footer
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {!isCompact && (
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            <Alert severity="success">Success alert</Alert>
            <Alert severity="warning">Warning alert</Alert>
            <Alert severity="error">Error alert</Alert>
            <Alert severity="info">Info alert</Alert>
          </Stack>
        )}

        <Card variant="outlined" sx={{ maxWidth: '100%' }}>
          <CardContent sx={{ py: isCompact ? 1 : 2, '&:last-child': { pb: isCompact ? 1 : 2 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Card + SCSS button
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uses scoped CSS variables for .sevv-btn-primary.
            </Typography>
            <button type="button" className="sevv-btn sevv-btn-primary sevv-btn-sm">
              SCSS primary
            </button>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  )
}

export default ThemePreviewPanel
